import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { CheckboxWithText } from "../../components/Checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, } from "../../components/Collapsible";
import { Tooltip } from "../../components/Tooltip";
import { cn } from "../../lib/utils";
import { cva } from "class-variance-authority";
import React, { useState } from "react";
const DEFAULT_APPLY_LABEL = "Apply";
const DEFAULT_REJECT_LABEL = "Reject";
const DEFAULT_CHECK_LABEL = "Always allow";
const DEFAULT_COLLAPSIBLE_LABEL = "Details";
const resolvedTitleClass = "italic font-normal text-muted-foreground mr-2";
const titleVariants = cva("", {
    variants: {
        size: {
            compact: "heading-sm",
            default: "heading-base",
        },
        status: {
            active: "text-foreground",
            disabled: "text-faint",
            accepted: resolvedTitleClass,
            rejected: resolvedTitleClass,
        },
    },
    compoundVariants: [
        { status: "accepted", size: "compact", className: "text-sm" },
        { status: "accepted", size: "default", className: "text-base" },
        { status: "rejected", size: "compact", className: "text-sm" },
        { status: "rejected", size: "default", className: "text-base" },
    ],
    defaultVariants: { size: "default", status: "active" },
});
const descriptionVariants = cva("", {
    variants: {
        size: {
            compact: "text-sm",
            default: "text-base",
        },
        status: {
            active: "text-muted-foreground",
            disabled: "text-faint",
            accepted: "text-faint",
            rejected: "text-faint",
        },
    },
    defaultVariants: { size: "default", status: "active" },
});
const subtitleVariants = cva("", {
    variants: {
        size: {
            compact: "text-sm",
            default: "text-base",
        },
        status: {
            active: "text-foreground",
            disabled: "text-faint",
            accepted: "text-faint",
            rejected: "text-faint",
        },
    },
    defaultVariants: { size: "default", status: "active" },
});
export function ActionCardBlock({ title, visual, subtitle, description, collapsibleContent, collapsibleLabel, actions, actionsPosition = "footer", applyLabel, rejectLabel, hasCheck, checkLabel, onClickAccept, onClickReject, state = "active", acceptedTitle, rejectedTitle, cardVariant, size = "default", }) {
    const [isChecked, setIsChecked] = useState(false);
    const isCompact = size === "compact";
    const applyVariant = cardVariant === "warning" ? "warning" : "highlight";
    const isAccepted = state === "accepted";
    const isRejected = state === "rejected";
    const isResolved = isAccepted || isRejected;
    const isDisabled = state === "disabled";
    const resolvedTitle = isAccepted
        ? (acceptedTitle !== null && acceptedTitle !== void 0 ? acceptedTitle : title)
        : isRejected
            ? (rejectedTitle !== null && rejectedTitle !== void 0 ? rejectedTitle : title)
            : title;
    const titleClasses = titleVariants({ size, status: state });
    const subtitleClasses = subtitleVariants({ size, status: state });
    const descriptionClasses = descriptionVariants({ size, status: state });
    const elementSize = isCompact ? "xs" : "sm";
    const resolvedVisual = visual
        ? React.cloneElement(visual, { size: elementSize })
        : null;
    const handleAcceptClick = () => {
        if (isDisabled || isResolved) {
            return;
        }
        onClickAccept === null || onClickAccept === void 0 ? void 0 : onClickAccept();
    };
    const handleRejectClick = () => {
        if (isDisabled || isResolved) {
            return;
        }
        onClickReject === null || onClickReject === void 0 ? void 0 : onClickReject();
    };
    const defaultActionButtons = (React.createElement("div", { className: "flex flex-wrap justify-end gap-2" },
        React.createElement(Button, { variant: "outline", size: elementSize, label: rejectLabel !== null && rejectLabel !== void 0 ? rejectLabel : DEFAULT_REJECT_LABEL, disabled: isDisabled, onClick: handleRejectClick }),
        React.createElement(Button, { variant: applyVariant, size: elementSize, label: applyLabel !== null && applyLabel !== void 0 ? applyLabel : DEFAULT_APPLY_LABEL, disabled: isDisabled, onClick: handleAcceptClick })));
    const actionButtons = actions !== null && actions !== void 0 ? actions : defaultActionButtons;
    const showHeader = resolvedVisual || resolvedTitle;
    const showActionsInHeader = !isResolved && actionsPosition === "header";
    const showActionsInFooter = !isResolved && actionsPosition === "footer";
    const tooltipLabel = isResolved ? description : undefined;
    const card = (React.createElement(Card, { variant: "primary", size: isCompact ? "sm" : "md", disabled: isDisabled, containerClassName: isResolved ? "max-w-lg w-fit" : "max-w-lg w-full", className: cn("flex-col", isCompact ? "gap-2" : "gap-3") },
        showHeader && (React.createElement("div", { className: "flex min-h-6 flex-wrap items-start justify-between gap-2" },
            React.createElement("div", { className: "flex min-w-0 items-center gap-2" },
                resolvedVisual,
                React.createElement("div", { className: "flex min-w-0 flex-col" },
                    resolvedTitle && (React.createElement("div", { className: titleClasses }, resolvedTitle)),
                    !isResolved && subtitle && (React.createElement("div", { className: subtitleClasses }, subtitle)))),
            showActionsInHeader && (React.createElement("div", { className: "ml-auto shrink-0" }, actionButtons)))),
        !isResolved && description && (React.createElement("div", { className: descriptionClasses }, description)),
        collapsibleContent && (React.createElement(Collapsible, null,
            React.createElement(CollapsibleTrigger, { className: "mb-1", label: collapsibleLabel !== null && collapsibleLabel !== void 0 ? collapsibleLabel : DEFAULT_COLLAPSIBLE_LABEL, variant: "secondary" }),
            React.createElement(CollapsibleContent, { className: isCompact ? "heading-xs" : "heading-sm" }, collapsibleContent))),
        showActionsInFooter && (React.createElement("div", { className: cn("flex flex-wrap gap-2", hasCheck ? "justify-between" : "justify-end") },
            hasCheck && (React.createElement(CheckboxWithText, { text: checkLabel !== null && checkLabel !== void 0 ? checkLabel : DEFAULT_CHECK_LABEL, checked: isChecked, disabled: isDisabled, onCheckedChange: (value) => setIsChecked(value === true) })),
            actionButtons))));
    return tooltipLabel ? (React.createElement(Tooltip, { label: tooltipLabel, tooltipTriggerAsChild: true, trigger: React.createElement("span", { className: "inline-block w-fit" },
            React.createElement("span", { className: "pointer-events-none" }, card)) })) : (card);
}
//# sourceMappingURL=ActionCardBlock.js.map